import React, { useCallback, useEffect, useMemo } from 'react';

import PropTypes from 'prop-types';
import { TextField } from '@picsio/ui';

import l10n from '../../../../shared/strings';
import Tooltip from '../../../Tooltip/Tooltip';
import productStyles from '../../styles.module.scss';
import { useBlurCommit, useEnterToBlur, useLocalFieldValue } from '../../hooks';
import {
  normalizeMediaValue,
  serializeMediaValue,
} from '../utils';
import styles from '../fields.module.scss';
import ProductFieldRow from './ProductFieldRow';

const NAME_PLACEHOLDER_BY_TYPE = {
  image: l10n.PRODUCT_INFO.placeholders.imageName,
  video: l10n.PRODUCT_INFO.placeholders.videoName,
  file: l10n.PRODUCT_INFO.placeholders.fileName,
};

const MediaField = ({
  data, value, type, disabled, isDiff, onChange,
}) => {
  const normalized = useMemo(() => normalizeMediaValue(value), [value]);
  const { localValue, setLocalValue } = useLocalFieldValue(normalized);

  useEffect(() => {
    setLocalValue(normalizeMediaValue(value));
  }, [setLocalValue, value]);

  const commit = useCallback((nextValue, onCancel) => {
    onChange(serializeMediaValue(nextValue), data, onCancel);
  }, [data, onChange]);

  const { handleBlur } = useBlurCommit({
    value: normalized,
    localValue,
    setLocalValue,
    onCommit: commit,
    serialize: serializeMediaValue,
  });

  const handleNameChange = useCallback((event) => {
    setLocalValue({ ...localValue, name: event.target.value });
  }, [localValue, setLocalValue]);

  const handleUrlChange = useCallback((event) => {
    setLocalValue({ ...localValue, url: event.target.value });
  }, [localValue, setLocalValue]);

  const handleKeyPress = useEnterToBlur();

  return (
    <ProductFieldRow title={data.title} className={productStyles.row}>
      <div className={styles.mediaField}>
        <TextField
          value={localValue.name}
          label={<Tooltip content={isDiff ? 'Unpublished changes' : null} placement="top"><span className={isDiff ? productStyles.diffDot : undefined}>{data.title}</span></Tooltip>}
          disabled={disabled}
          readOnly={disabled}
          placeholder={NAME_PLACEHOLDER_BY_TYPE[type]}
          onChange={handleNameChange}
          onBlur={handleBlur}
          onKeyPress={handleKeyPress}
        />
        <TextField
          value={localValue.url}
          disabled={disabled}
          readOnly={disabled}
          placeholder={l10n.PRODUCT_INFO.placeholders.linkUrl}
          onChange={handleUrlChange}
          onBlur={handleBlur}
          onKeyPress={handleKeyPress}
        />
        <If condition={type === 'image' && localValue.url}>
          <img
            className={styles.mediaPreview}
            src={localValue.url}
            alt={localValue.name || data.title}
          />
        </If>
      </div>
    </ProductFieldRow>
  );
};

MediaField.propTypes = {
  data: PropTypes.shape({ title: PropTypes.string }).isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  type: PropTypes.oneOf(['file', 'image', 'video']).isRequired,
  disabled: PropTypes.bool,
  isDiff: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

MediaField.defaultProps = {
  value: '',
  disabled: false,
  isDiff: false,
};

export default MediaField;
